import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { useAuth } from "../auth";
import { useCart } from "../cart";
import {
  trackBeginCheckout,
  trackCheckoutValidationError,
  trackPaymentError,
  trackPaymentMethodSelected,
  trackPurchase,
  trackShippingInsuranceSelected,
} from "../analytics";
import {
  API_ROOT,
  apiJson,
  currency,
  getDiscount,
  getShippingCost,
  getTaxAmount,
  SHIPPING_METHODS,
  TAX_RATES,
} from "../shared";

/**********************
 * Checkout — real PayPal (server-driven), saves order to DB
 *********************/
function CheckoutPage() {
  const { state, setShipping, clearCart } = useCart();
  const { user, authFetch } = useAuth();
  const nav = useNavigate();
  const items = Object.entries(state.items);
  const insuranceTotal = items.reduce((sum, [key, it]) => {
    const perItem = Number(it?.insuranceCost) || 0;
    const qty = Number(it?.qty) || 0;
    return sum + perItem * qty;
  }, 0);
  const subtotal = items.reduce(
    (sum, [key, it]) => sum + it.unitPrice * it.qty,
    0,
  );
  const discount = getDiscount(subtotal, state.coupon);
  const shippingCostBase = getShippingCost(
    subtotal - discount,
    state.shipping,
    state.coupon,
  );
  const shippingCost =
    (state.shipping?.method || "basic") === "insured"
      ? shippingCostBase + insuranceTotal
      : shippingCostBase;
  const taxable = Math.max(0, subtotal - discount);
  const tax = getTaxAmount(taxable, state.shipping?.state);
  const total = Math.max(0, taxable + tax + shippingCost);

  const [sqConfig, setSqConfig] = useState(null);
  const [sqReady, setSqReady] = useState(false);
  const [sqErr, setSqErr] = useState("");
  const [sqProcessing, setSqProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const sqPaymentsRef = useRef(null);
  const sqCardRef = useRef(null);
  const sqApplePayRef = useRef(null);
  const sqInitKeyRef = useRef(null);

  const SQUARE_MOCK_PAYMENTS =
    String(process.env.REACT_APP_SQUARE_MOCK_PAYMENTS || "").toLowerCase() ===
    "true";

  const SHOW_SQUARE_TEST_HELPER =
    String(
      process.env.REACT_APP_SHOW_SQUARE_TEST_HELPER || "",
    ).toLowerCase() === "true";

  useEffect(() => {
    console.log("[Square] config", sqConfig);
  }, [sqConfig]);

  useEffect(() => {
    console.log("[Square] ready", sqReady);
  }, [sqReady]);

  useEffect(() => {
    if (sqErr) console.log("[Square] error", sqErr);
  }, [sqErr]);

  const [mockCardNumber, setMockCardNumber] = useState("");

  const copyToClipboard = async (text) => {
    const str = String(text ?? "");
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(str);
        return;
      }
    } catch {}

    try {
      window.prompt("Copy to clipboard:", str);
    } catch {}
  };

  const [form, setForm] = useState({
    email: user?.email || "",
    first: user?.first || "",
    last: user?.last || "",
    address1: "",
    address2: "",
    city: "",
    state: state.shipping?.state || "TX",
    zip: state.shipping?.zip || "",
    country: state.shipping?.country || "US",
    method: state.shipping?.method || "basic",
    payMethod: "paypal",
  });

  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";

  const [shipErr, setShipErr] = useState("");
  const [shipMissing, setShipMissing] = useState({});

  const shippingHydratedRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    if (!user?.id) {
      shippingHydratedRef.current = false;
      return;
    }

    (async () => {
      try {
        const saved = await authFetch("/users/shipping");
        if (!mounted) return;
        if (!saved || typeof saved !== "object") {
          shippingHydratedRef.current = true;
          return;
        }

        const normalizeMethod = (m) => {
          const mm = String(m || "")
            .trim()
            .toLowerCase();
          if (mm === "standard") return "basic";
          if (mm === "basic" || mm === "insured") return mm;
          return "basic";
        };

        setForm((f) => ({
          ...f,
          email: saved.email ?? f.email,
          first: saved.first ?? f.first,
          last: saved.last ?? f.last,
          address1: saved.address1 ?? f.address1,
          address2: saved.address2 ?? f.address2,
          city: saved.city ?? f.city,
          state: saved.state ?? f.state,
          zip: saved.zip ?? f.zip,
          country: saved.country ?? f.country,
          method: normalizeMethod(
            state.shipping?.method || saved.method || f.method,
          ),
        }));
        shippingHydratedRef.current = true;
      } catch {
        shippingHydratedRef.current = true;
      }
    })();

    return () => {
      mounted = false;
    };
  }, [user?.id, authFetch, state.shipping?.method]);

  useEffect(() => {
    setShipping({
      state: form.state,
      zip: form.zip,
      method: form.method,
      country: form.country,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.state, form.zip, form.method, form.country]);

  useEffect(() => {
    if (!GA4_ID) return;

    const isInsured = (form.method || "basic") === "insured";
    if (!isInsured) {
      try {
        sessionStorage.removeItem("ga4:insured_selected");
      } catch {}
      return;
    }

    let alreadyTracked = false;
    try {
      alreadyTracked =
        sessionStorage.getItem("ga4:insured_selected") === "1" || false;
    } catch {}
    if (alreadyTracked) return;

    try {
      trackShippingInsuranceSelected({
        measurementId: GA4_ID,
        insuranceTotal,
        cartValue: total,
        itemsCount: items?.length || 0,
      });
      try {
        sessionStorage.setItem("ga4:insured_selected", "1");
      } catch {}
    } catch {}
  }, [GA4_ID, form.method, insuranceTotal, items?.length, total]);

  useEffect(() => {
    if (!GA4_ID) return;
    if (!items?.length) return;

    let alreadyTracked = false;
    try {
      alreadyTracked =
        sessionStorage.getItem("ga4:begin_checkout") === "1" || false;
    } catch {}
    if (alreadyTracked) return;

    try {
      trackBeginCheckout({
        measurementId: GA4_ID,
        value: total,
        coupon: state.coupon?.code,
        items: items.map(([k, it]) => ({
          item_id: String(it.id || it.sku || ""),
          item_name: String(it.name || ""),
          price: Number(it.unitPrice) || 0,
          quantity: Number(it.qty) || 1,
        })),
      });
      try {
        sessionStorage.setItem("ga4:begin_checkout", "1");
      } catch {}
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!GA4_ID) return;
    try {
      trackPaymentMethodSelected({
        measurementId: GA4_ID,
        method:
          paymentMethod === "card"
            ? "square_card"
            : paymentMethod === "paypal"
              ? "paypal"
              : paymentMethod === "applepay"
                ? "apple_pay"
                : paymentMethod,
        isGuest: !user,
      });
    } catch {}
  }, [GA4_ID, paymentMethod, user]);

  useEffect(() => {
    if (!user?.id) return;
    if (!shippingHydratedRef.current) return;

    const t = setTimeout(() => {
      authFetch("/users/shipping", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email,
          first: form.first,
          last: form.last,
          address1: form.address1,
          address2: form.address2,
          city: form.city,
          state: form.state,
          zip: form.zip,
          country: form.country,
          method: form.method,
        }),
      }).catch(() => {});
    }, 500);

    return () => clearTimeout(t);
  }, [
    user?.id,
    authFetch,
    form.email,
    form.first,
    form.last,
    form.address1,
    form.address2,
    form.city,
    form.state,
    form.zip,
    form.country,
    form.method,
  ]);

  const buildBreakdown = () => ({
    item_total: { value: subtotal.toFixed(2), currency_code: "USD" },
    shipping: { value: shippingCost.toFixed(2), currency_code: "USD" },
    tax_total: { value: tax.toFixed(2), currency_code: "USD" },
    discount: { value: discount.toFixed(2), currency_code: "USD" },
  });

  const validateShipping = () => {
    const missing = {};
    const f = formRef.current || {};
    const email = String(f.email || "").trim();
    const first = String(f.first || "").trim();
    const last = String(f.last || "").trim();
    const address1 = String(f.address1 || "").trim();
    const city = String(f.city || "").trim();
    const stateCode = String(f.state || "").trim();
    const zip = String(f.zip || "").trim();

    if (!email) missing.email = true;
    if (!first) missing.first = true;
    if (!last) missing.last = true;
    if (!address1) missing.address1 = true;
    if (!city) missing.city = true;
    if (!stateCode) missing.state = true;
    if (!zip) missing.zip = true;

    if (Object.keys(missing).length) {
      const msg = missing.first
        ? "Please enter your first name."
        : missing.last
          ? "Please enter your last name."
          : missing.email
            ? "Please enter your email."
            : missing.address1
              ? "Please enter your address."
              : missing.city
                ? "Please enter your city."
                : missing.state
                  ? "Please select your state."
                  : "Please enter your ZIP code.";
      return { ok: false, msg, missing };
    }

    return { ok: true };
  };

  const shipFieldStyle = (key) =>
    shipMissing?.[key]
      ? { borderColor: "#b91c1c", boxShadow: "0 0 0 1px #b91c1c inset" }
      : undefined;

  const applePayAvailable =
    !!sqApplePayRef.current && sqReady && !SQUARE_MOCK_PAYMENTS;

  const clearShipMissing = (key) => {
    setShipMissing((m) => {
      if (!m || !m[key]) return m;
      const next = { ...m };
      delete next[key];
      return next;
    });
  };

  const createOrderServer = async () => {
    if (!API_ROOT) {
      alert("Set REACT_APP_API_ROOT in your React .env");
      throw new Error("Missing API_ROOT");
    }

    const v = validateShipping();
    if (!v.ok) {
      setShipErr(v.msg);
      setShipMissing(v.missing || {});
      try {
        if (GA4_ID) {
          trackCheckoutValidationError({
            measurementId: GA4_ID,
            missingFields: Object.keys(v.missing || {}),
            methodAttempted: "paypal",
            isGuest: !user,
          });
        }
      } catch {}
      alert(v.msg);
      throw new Error(v.msg);
    }

    console.log("[PayPal] createOrder (server) start", {
      apiRoot: API_ROOT,
      total,
      currency: "USD",
      breakdown: buildBreakdown(),
    });
    const res = await apiJson(API_ROOT, "/paypal/create-order", {
      method: "POST",
      body: JSON.stringify({
        amount: total.toFixed(2),
        currency: "USD",
        breakdown: buildBreakdown(),
        items: items.map(([k, it]) => ({
          productId: it.id,
          qty: it.qty,
          metal: it.metal,
          karat: (() => {
            const metalStr = it.metal || "";
            const maybeKarat = String(metalStr).trim().split(/\s+/)[0];
            return /\d+k/i.test(maybeKarat) ? maybeKarat : undefined;
          })(),
          stoneType: it.stoneType,
          size: it.size,
        })),
        shipping: state.shipping,
        coupon: state.coupon,
      }),
    });
    console.log("[PayPal] createOrder (server) ok", { orderId: res?.id });
    return res.id;
  };

  const captureOrderServer = async (orderID) => {
    console.log("[PayPal] captureOrder (server) start", {
      apiRoot: API_ROOT,
      orderID,
      loggedIn: !!user,
      userId: user?.id,
    });
    const cap = await apiJson(API_ROOT, "/paypal/capture-order", {
      method: "POST",
      body: JSON.stringify({ orderID }),
    });
    const payerEmail = cap?.payer?.email_address;
    const captureId = cap?.purchase_units?.[0]?.payments?.captures?.[0]?.id;
    const status = cap?.status;
    console.log("[PayPal] captureOrder (server) ok", {
      orderID,
      status,
      captureId,
      payerEmail,
    });
    const orderPayload = {
      items: items.map(([k, it]) => ({
        productId: it.id,
        name: it.name,
        image: it.image,
        size: String(it.size || ""),
        unitPrice: it.unitPrice,
        qty: it.qty,
        metal: it.metal,
        karat: (() => {
          const metalStr = it.metal || "";
          const maybeKarat = String(metalStr).trim().split(/\s+/)[0];
          return /\d+k/i.test(maybeKarat) ? maybeKarat : undefined;
        })(),
        stoneType: it.stoneType,
      })),
      pricing: { subtotal, discount, tax, shipping: shippingCost, total },
      shipping: { ...form },
      paypal: { orderId: orderID, captureId, payerEmail, status, raw: cap },
    };

    console.log("[PayPal] save order start", {
      route: user ? "/orders" : "/orders/guest",
      buyerEmail: form?.email,
      itemsCount: Array.isArray(orderPayload.items)
        ? orderPayload.items.length
        : 0,
      total: orderPayload?.pricing?.total,
    });
    const saveRes = user
      ? await authFetch("/orders", {
          method: "POST",
          body: JSON.stringify(orderPayload),
        })
      : await apiJson(API_ROOT, "/orders/guest", {
          method: "POST",
          body: JSON.stringify(orderPayload),
        });
    console.log("[PayPal] save order ok", { orderDbId: saveRes?.id });
    return { payerEmail, captureId, status, orderDbId: saveRes.id, cap };
  };

  useEffect(() => {
    let mounted = true;
    console.log("SQUARE_MOCK_PAYMENTS", SQUARE_MOCK_PAYMENTS);

    if (!API_ROOT) return;
    apiJson(API_ROOT, "/square/config")
      .then((cfg) => {
        if (mounted) setSqConfig(cfg);
      })
      .catch(() => {
        if (mounted) setSqConfig(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const appId = sqConfig?.applicationId;
    const locationId = sqConfig?.locationId;
    if (!appId || !locationId) return;

    const initKey = `${String(appId)}@${String(locationId)}@${String(sqConfig?.environment || "")}`;
    if (sqInitKeyRef.current === initKey && sqCardRef.current) return;

    const src =
      sqConfig?.environment === "production"
        ? "https://web.squarecdn.com/v1/square.js"
        : "https://sandbox.web.squarecdn.com/v1/square.js";

    const load = () =>
      new Promise((resolve, reject) => {
        if (window.Square) return resolve();

        const existing = document.querySelector(`script[src="${src}"]`);
        const s =
          existing ||
          (() => {
            const tag = document.createElement("script");
            tag.src = src;
            tag.async = true;
            document.body.appendChild(tag);
            return tag;
          })();

        const startedAt = Date.now();
        const timeoutMs = 10000;

        const check = () => {
          if (window.Square) return resolve();
          if (Date.now() - startedAt > timeoutMs) {
            return reject(new Error("Square SDK not available"));
          }
          setTimeout(check, 50);
        };

        s.addEventListener("error", () => {
          reject(new Error("Failed to load Square"));
        });

        console.log("[Square] loading SDK", {
          src,
          alreadyPresent: !!existing,
        });

        check();
      });

    (async () => {
      try {
        setSqErr("");
        setSqReady(false);
        await load();
        if (!mounted) return;
        if (!window.Square) throw new Error("Square SDK not available");

        const payments = window.Square.payments(appId, locationId);
        sqPaymentsRef.current = payments;

        try {
          if (sqCardRef.current?.destroy) await sqCardRef.current.destroy();
        } catch {}
        try {
          const el = document.querySelector("#square-card-container");
          if (el) el.innerHTML = "";
        } catch {}

        const card = await payments.card();
        await card.attach("#square-card-container");
        sqCardRef.current = card;

        try {
          const req = payments.paymentRequest({
            countryCode: "US",
            currencyCode: "USD",
            total: {
              amount: total.toFixed(2),
              label: "Total",
            },
          });
          const applePay = await payments.applePay(req);
          sqApplePayRef.current = applePay;
          console.log("[Square] Apple Pay ready", {
            hasApplePayInstance: !!sqApplePayRef.current,
          });
        } catch (e) {
          sqApplePayRef.current = null;
          console.log("[Square] Apple Pay unavailable", {
            hasApplePayInstance: !!sqApplePayRef.current,
            error: String(e?.message || e),
          });
        }

        setSqReady(true);
        sqInitKeyRef.current = initKey;
      } catch (e) {
        setSqErr(String(e.message || e));
      }
    })();

    return () => {
      mounted = false;
    };
  }, [sqConfig?.applicationId, sqConfig?.locationId, sqConfig?.environment]);

  const finalizeSquareOrder = async (paymentMeta, forcedOrderId) => {
    const orderId =
      forcedOrderId || `RID-${Date.now().toString(36).toUpperCase()}`;
    console.log("[Square] finalizeSquareOrder", {
      orderId,
      paymentId: paymentMeta?.id,
      status: paymentMeta?.status,
    });
    try {
      try {
        const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
        if (GA4_ID) {
          trackPurchase({
            measurementId: GA4_ID,
            transactionId: orderId,
            value: total,
            tax,
            shipping: shippingCost,
            coupon: state.coupon?.code,
            items: items.map(([k, it]) => ({
              item_id: String(it.id || it.sku || ""),
              item_name: String(it.name || ""),
              price: Number(it.unitPrice) || 0,
              quantity: Number(it.qty) || 1,
            })),
          });
        }
      } catch {}
      sessionStorage.setItem(
        `order:${orderId}`,
        JSON.stringify({
          id: orderId,
          at: new Date().toISOString(),
          items: items.map(([k, it]) => it),
          pricing: {
            subtotal,
            discount,
            tax,
            shipping: shippingCost,
            total,
          },
          shipping: { ...form },
          payment: {
            method: "square",
            squarePaymentId: paymentMeta?.id,
            status: paymentMeta?.status,
          },
        }),
      );
    } catch {}
    clearCart();
    nav(`/order/${orderId}`);
  };

  const payWithSquareCard = async () => {
    console.log("payWithSquareCard");

    try {
      if (sqProcessing) return;

      const v = validateShipping();
      if (!v.ok) {
        setShipErr(v.msg);
        setShipMissing(v.missing || {});
        setSqErr(v.msg);
        try {
          if (GA4_ID) {
            trackCheckoutValidationError({
              measurementId: GA4_ID,
              missingFields: Object.keys(v.missing || {}),
              methodAttempted: "square_card",
              isGuest: !user,
            });
          }
        } catch {}
        return;
      }

      console.log("[Square] payWithSquareCard clicked", {
        apiRoot: API_ROOT,
        sqReady,
        hasCardInstance: !!sqCardRef.current,
        total,
        email: form.email,
        mock: SQUARE_MOCK_PAYMENTS,
      });
      setSqErr("");

      if (SQUARE_MOCK_PAYMENTS) {
        console.log("start");

        const ridOrderId = `RID-${Date.now().toString(36).toUpperCase()}`;
        const digits = String(mockCardNumber || "").replace(/\D/g, "");
        if (digits.length < 12) {
          setSqErr("Enter a card number to submit the mock payment.");
          return;
        }

        setSqProcessing(true);
        const mockCard = {
          bin: digits.slice(0, 6),
          last4: digits.slice(-4),
        };
        const mockItems = items.map(([k, it]) => {
          const metalStr = it.metal || "";
          const maybeKarat = String(metalStr).trim().split(/\s+/)[0];
          const karat = /\d+k/i.test(maybeKarat) ? maybeKarat : undefined;
          return {
            name: it.name,
            qty: it.qty,
            unitPrice: it.unitPrice,
            metal: it.metal,
            karat,
            size: it.size,
            stoneType: it.stoneType,
            note: `productId=${it.id} size=${String(it.size || "")} metal=${String(it.metal || "")} stoneType=${String(it.stoneType || "")}`,
          };
        });

        console.log("go");

        const orderRes = await apiJson(API_ROOT, "/square/orders", {
          method: "POST",
          body: JSON.stringify({
            mock: true,
            referenceId: ridOrderId,
            userId: user?.id || undefined,
            buyerEmail: form.email,
            buyerName:
              `${String(form.first || "").trim()} ${String(form.last || "").trim()}`.trim(),
            shipping: { ...form },
            note: "Mock order",
            items: mockItems,
            pricing: {
              subtotal,
              discount,
              tax,
              shipping: shippingCost,
              total,
            },
          }),
        });
        console.log("[Square] /square/orders mock response", orderRes);

        const payRes = await apiJson(API_ROOT, "/square/payments", {
          method: "POST",
          body: JSON.stringify({
            mock: true,
            amount: total,
            currency: "USD",
            buyerEmail: form.email,
            buyerName:
              `${String(form.first || "").trim()} ${String(form.last || "").trim()}`.trim(),
            userId: user?.id || undefined,
            orderId: orderRes?.order?.id || ridOrderId,
            referenceId: ridOrderId,
            mockCard,
            shipping: { ...form },
            items: mockItems,
            pricing: {
              subtotal,
              discount,
              tax,
              shipping: shippingCost,
              total,
            },
          }),
        });
        console.log("[Square] /square/payments mock response", payRes);

        await finalizeSquareOrder(payRes.payment, ridOrderId);
        return;
      }

      if (!sqCardRef.current) throw new Error("Square card not ready");

      const tok = await sqCardRef.current.tokenize();
      console.log("[Square] tokenize result", tok);
      if (tok.status !== "OK") {
        setSqErr(tok.errors?.[0]?.message || "Please enter your card details.");
        try {
          if (GA4_ID) {
            trackPaymentError({
              measurementId: GA4_ID,
              provider: "square",
              stage: "tokenize",
              methodAttempted: "square_card",
              message:
                tok.errors?.[0]?.message || "Square tokenize failed (card)",
            });
          }
        } catch {}
        return;
      }

      setSqProcessing(true);

      const ridOrderId = `RID-${Date.now().toString(36).toUpperCase()}`;
      const liveItems = items.map(([k, it]) => {
        const metalStr = it.metal || "";
        const maybeKarat = String(metalStr).trim().split(/\s+/)[0];
        const karat = /\d+k/i.test(maybeKarat) ? maybeKarat : undefined;
        return {
          name: it.name,
          qty: it.qty,
          unitPrice: it.unitPrice,
          metal: it.metal,
          karat,
          size: it.size,
          stoneType: it.stoneType,
          note: `productId=${it.id} size=${String(it.size || "")} metal=${String(it.metal || "")} stoneType=${String(it.stoneType || "")}`,
        };
      });

      const orderRes = await apiJson(API_ROOT, "/square/orders", {
        method: "POST",
        body: JSON.stringify({
          referenceId: ridOrderId,
          userId: user?.id || undefined,
          buyerEmail: form.email,
          buyerName:
            `${String(form.first || "").trim()} ${String(form.last || "").trim()}`.trim(),
          shipping: { ...form },
          note: "Order",
          items: liveItems,
          pricing: {
            subtotal,
            discount,
            tax,
            shipping: shippingCost,
            total,
          },
        }),
      });
      console.log("[Square] /square/orders response", orderRes);
      const res = await apiJson(API_ROOT, "/square/payments", {
        method: "POST",
        body: JSON.stringify({
          sourceId: tok.token,
          amount: total,
          currency: "USD",
          buyerEmail: form.email,
          buyerName:
            `${String(form.first || "").trim()} ${String(form.last || "").trim()}`.trim(),
          userId: user?.id || undefined,
          shipping: { ...form },
          items: liveItems,
          pricing: {
            subtotal,
            discount,
            tax,
            shipping: shippingCost,
            total,
          },
          orderId: orderRes?.order?.id,
          referenceId: ridOrderId,
        }),
      });
      console.log("[Square] /square/payments response", res);
      await finalizeSquareOrder(res.payment, ridOrderId);
    } catch (e) {
      console.error("[Square] payWithSquareCard error", e);
      setSqErr(String(e.message || e));
      try {
        if (GA4_ID) {
          trackPaymentError({
            measurementId: GA4_ID,
            provider: "square",
            stage: "pay_with_card",
            methodAttempted: "square_card",
            message: String(e.message || e),
          });
        }
      } catch {}
    } finally {
      setSqProcessing(false);
    }
  };

  const payWithSquareApplePay = async () => {
    try {
      if (sqProcessing) return;

      const v = validateShipping();
      if (!v.ok) {
        setShipErr(v.msg);
        setSqErr(v.msg);
        try {
          if (GA4_ID) {
            trackCheckoutValidationError({
              measurementId: GA4_ID,
              missingFields: Object.keys(v.missing || {}),
              methodAttempted: "apple_pay",
              isGuest: !user,
            });
          }
        } catch {}
        return;
      }

      setSqProcessing(true);
      console.log("[Square] payWithSquareApplePay clicked", {
        apiRoot: API_ROOT,
        sqReady,
        hasApplePayInstance: !!sqApplePayRef.current,
        total,
        email: form.email,
      });
      setSqErr("");
      if (!sqApplePayRef.current) throw new Error("Apple Pay not available");

      const tok = await sqApplePayRef.current.tokenize();
      console.log("[Square] apple pay tokenize result", tok);
      if (tok.status !== "OK") {
        try {
          if (GA4_ID) {
            trackPaymentError({
              measurementId: GA4_ID,
              provider: "square",
              stage: "tokenize",
              methodAttempted: "apple_pay",
              message:
                tok.errors?.[0]?.message ||
                "Square tokenize failed (apple pay)",
            });
          }
        } catch {}
        throw new Error(
          tok.errors?.[0]?.message || "Apple Pay tokenize failed",
        );
      }

      const ridOrderId = `RID-${Date.now().toString(36).toUpperCase()}`;
      const liveItems = items.map(([k, it]) => {
        const metalStr = it.metal || "";
        const maybeKarat = String(metalStr).trim().split(/\s+/)[0];
        const karat = /\d+k/i.test(maybeKarat) ? maybeKarat : undefined;
        return {
          name: it.name,
          qty: it.qty,
          unitPrice: it.unitPrice,
          metal: it.metal,
          karat,
          size: it.size,
          stoneType: it.stoneType,
          note: `productId=${it.id} size=${String(it.size || "")} metal=${String(it.metal || "")} stoneType=${String(it.stoneType || "")}`,
        };
      });

      const orderRes = await apiJson(API_ROOT, "/square/orders", {
        method: "POST",
        body: JSON.stringify({
          referenceId: ridOrderId,
          userId: user?.id || undefined,
          buyerEmail: form.email,
          buyerName:
            `${String(form.first || "").trim()} ${String(form.last || "").trim()}`.trim(),
          shipping: { ...form },
          note: "Order",
          items: liveItems,
          pricing: {
            subtotal,
            discount,
            tax,
            shipping: shippingCost,
            total,
          },
        }),
      });
      console.log("[Square] /square/orders response (apple pay)", orderRes);

      const res = await apiJson(API_ROOT, "/square/payments", {
        method: "POST",
        body: JSON.stringify({
          sourceId: tok.token,
          amount: total,
          currency: "USD",
          buyerEmail: form.email,
          buyerName:
            `${String(form.first || "").trim()} ${String(form.last || "").trim()}`.trim(),
          userId: user?.id || undefined,
          shipping: { ...form },
          items: liveItems,
          pricing: {
            subtotal,
            discount,
            tax,
            shipping: shippingCost,
            total,
          },
          orderId: orderRes?.order?.id,
          referenceId: ridOrderId,
        }),
      });
      console.log("[Square] /square/payments response (apple pay)", res);
      await finalizeSquareOrder(res.payment, ridOrderId);
    } catch (e) {
      console.error("[Square] payWithSquareApplePay error", e);
      setSqErr(String(e.message || e));
      try {
        if (GA4_ID) {
          trackPaymentError({
            measurementId: GA4_ID,
            provider: "square",
            stage: "pay_with_apple_pay",
            methodAttempted: "apple_pay",
            message: String(e.message || e),
          });
        }
      } catch {}
    } finally {
      setSqProcessing(false);
    }
  };

  return (
    <section className="jh-container jh-section jh-checkout">
      {sqProcessing && (
        <div className="jh-processing-overlay" aria-live="polite">
          <div className="jh-processing-card">
            <div className="jh-spinner" />
            <div className="jh-processing-title">Processing payment</div>
            <div className="jh-processing-sub">
              Please don’t refresh or close this tab.
            </div>
          </div>
        </div>
      )}
      <h1 className="jh-title">Checkout</h1>

      {!user && (
        <div className="jh-note" style={{ marginBottom: 12 }}>
          Have an account? <Link to="/login">Sign in</Link> to save your cart
          and checkout faster.
        </div>
      )}

      <div className="jh-card jh-pane">
        <h2 className="jh-pane-title">Shipping Details</h2>
        {shipErr && (
          <div className="jh-note" style={{ color: "#b91c1c" }}>
            {shipErr}
          </div>
        )}
        <div className="jh-form-grid" style={{ maxWidth: 720 }}>
          <div className="jh-two">
            <div className="jh-field">
              <label className="jh-label">First</label>
              <input
                className="jh-input wide"
                value={form.first}
                onChange={(e) => {
                  clearShipMissing("first");
                  setForm((f) => ({ ...f, first: e.target.value }));
                }}
                style={shipFieldStyle("first")}
              />
            </div>
            <div className="jh-field">
              <label className="jh-label">Last</label>
              <input
                className="jh-input wide"
                value={form.last}
                onChange={(e) => {
                  clearShipMissing("last");
                  setForm((f) => ({ ...f, last: e.target.value }));
                }}
                style={shipFieldStyle("last")}
              />
            </div>
          </div>
          <div className="jh-field">
            <label className="jh-label">Email</label>
            <input
              className="jh-input wide"
              value={form.email}
              onChange={(e) => {
                clearShipMissing("email");
                setForm((f) => ({ ...f, email: e.target.value }));
              }}
              placeholder="you@example.com"
              style={shipFieldStyle("email")}
            />
          </div>
          <div className="jh-field">
            <label className="jh-label">Address</label>
            <input
              className="jh-input wide"
              value={form.address1}
              onChange={(e) => {
                clearShipMissing("address1");
                setForm((f) => ({ ...f, address1: e.target.value }));
              }}
              placeholder="Street address"
              style={shipFieldStyle("address1")}
            />
          </div>
          <div className="jh-field">
            <label className="jh-label">Address 2</label>
            <input
              className="jh-input wide"
              value={form.address2}
              onChange={(e) =>
                setForm((f) => ({ ...f, address2: e.target.value }))
              }
              placeholder="Apt, suite, etc."
            />
          </div>
          <div className="jh-three">
            <div className="jh-field">
              <label className="jh-label">City</label>
              <input
                className="jh-input wide"
                value={form.city}
                onChange={(e) => {
                  clearShipMissing("city");
                  setForm((f) => ({ ...f, city: e.target.value }));
                }}
                style={shipFieldStyle("city")}
              />
            </div>
            <div className="jh-field">
              <label className="jh-label">State</label>
              <select
                className="jh-input wide"
                value={form.state}
                onChange={(e) => {
                  clearShipMissing("state");
                  setForm((f) => ({ ...f, state: e.target.value }));
                }}
                style={shipFieldStyle("state")}
              >
                {Object.keys(TAX_RATES).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="jh-field">
              <label className="jh-label">ZIP</label>
              <input
                className="jh-input wide"
                value={form.zip}
                onChange={(e) => {
                  clearShipMissing("zip");
                  setForm((f) => ({ ...f, zip: e.target.value }));
                }}
                style={shipFieldStyle("zip")}
              />
            </div>
          </div>
          <div className="jh-two">
            <div className="jh-field">
              <label className="jh-label">Country</label>
              <select className="jh-input wide" value="US" disabled>
                <option value="US">United States</option>
              </select>
            </div>
            <div className="jh-field">
              <label className="jh-label">Shipping method</label>
              <select
                className="jh-input wide"
                value={form.method}
                onChange={(e) =>
                  setForm((f) => ({ ...f, method: e.target.value }))
                }
              >
                {SHIPPING_METHODS.map((m) => {
                  const effectiveCost =
                    m.id === "insured"
                      ? (m.cost || 0) + insuranceTotal
                      : m.cost;
                  return (
                    <option key={m.id} value={m.id}>
                      {m.label}{" "}
                      {m.id !== "standard"
                        ? `(${currency(effectiveCost)})`
                        : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="jh-card jh-pane" style={{ maxWidth: 520 }}>
        <h2 className="jh-pane-title">Order total</h2>
        <div className="jh-cart-row">
          <span>Subtotal</span>
          <strong>{currency(subtotal)}</strong>
        </div>
        {discount > 0 && (
          <div className="jh-cart-row">
            <span>Discount</span>
            <strong>-{currency(discount)}</strong>
          </div>
        )}
        <div className="jh-cart-row">
          <span>Tax</span>
          <strong>{currency(tax)}</strong>
        </div>
        <div className="jh-cart-row">
          <span>Shipping</span>
          <strong>{currency(shippingCost)}</strong>
        </div>
        <div className="jh-cart-row total">
          <span>Total</span>
          <strong>{currency(total)}</strong>
        </div>
      </div>

      <div
        className="jh-card jh-pane"
        style={{ margin: "0 auto", marginBottom: 30 }}
      >
        <h2 className="jh-pane-title" style={{ textAlign: "center" }}>
          Payment method
        </h2>
        <div
          className="jh-actions"
          style={{ marginTop: 10, justifyContent: "center" }}
        >
          <button
            type="button"
            className={
              paymentMethod === "card" ? "jh-btn" : "jh-btn jh-btn--ghost"
            }
            onClick={() => setPaymentMethod("card")}
            disabled={sqProcessing}
          >
            Card
          </button>
          <button
            type="button"
            className={
              paymentMethod === "paypal" ? "jh-btn" : "jh-btn jh-btn--ghost"
            }
            onClick={() => setPaymentMethod("paypal")}
            disabled={sqProcessing}
          >
            PayPal
          </button>
          {applePayAvailable && (
            <button
              type="button"
              className={
                paymentMethod === "applepay" ? "jh-btn" : "jh-btn jh-btn--ghost"
              }
              onClick={() => {
                console.log("[Square] Apple Pay selector clicked", {
                  sqReady,
                  hasApplePayInstance: !!sqApplePayRef.current,
                  mock: SQUARE_MOCK_PAYMENTS,
                  sqProcessing,
                });
                setPaymentMethod("applepay");
              }}
              disabled={sqProcessing}
            >
              Apple Pay
            </button>
          )}
        </div>
      </div>

      <div
        className="jh-card jh-pane"
        style={{ display: paymentMethod === "card" ? "block" : "none" }}
      >
        <h2 className="jh-pane-title">Pay with Card</h2>
        <div className="jh-note" style={{ marginTop: 6 }}>
          Handmade to order — please allow 2–3 weeks.
        </div>
        {!API_ROOT && (
          <div className="jh-note" style={{ color: "#b91c1c" }}>
            Set REACT_APP_API_ROOT in your React .env to enable payments.
          </div>
        )}
        {sqErr && (
          <div className="jh-note" style={{ color: "#b91c1c" }}>
            {sqErr}
          </div>
        )}
        {SQUARE_MOCK_PAYMENTS && (
          <div className="jh-field" style={{ marginTop: 10 }}>
            <label className="jh-label">Card number (mock)</label>
            <input
              className="jh-input wide"
              value={mockCardNumber}
              onChange={(e) => setMockCardNumber(e.target.value)}
              placeholder="4111 1111 1111 1111"
              disabled={sqProcessing}
            />
          </div>
        )}
        {SHOW_SQUARE_TEST_HELPER && !SQUARE_MOCK_PAYMENTS && (
          <div
            className="jh-note"
            style={{
              marginTop: 10,
              border: "1px solid #e2e8f0",
              borderRadius: 12,
              padding: 12,
              background: "#f8fafc",
              color: "#0f172a",
            }}
          >
            <div style={{ fontWeight: 800, marginBottom: 8 }}>
              Square sandbox test card
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              <button
                type="button"
                className="jh-btn jh-btn--ghost"
                onClick={() => copyToClipboard("4111 1111 1111 1111")}
                disabled={sqProcessing}
              >
                Copy card number (4111 1111 1111 1111)
              </button>
              <button
                type="button"
                className="jh-btn jh-btn--ghost"
                onClick={() => copyToClipboard("12/30")}
                disabled={sqProcessing}
              >
                Copy exp (12/30)
              </button>
              <button
                type="button"
                className="jh-btn jh-btn--ghost"
                onClick={() => copyToClipboard("111")}
                disabled={sqProcessing}
              >
                Copy CVV (111)
              </button>
              <button
                type="button"
                className="jh-btn jh-btn--ghost"
                onClick={() => copyToClipboard("10001")}
                disabled={sqProcessing}
              >
                Copy ZIP (10001)
              </button>
            </div>
          </div>
        )}
        <div id="square-card-container" style={{ marginTop: 10 }} />
        <div className="jh-actions" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="jh-btn"
            onClick={payWithSquareCard}
            disabled={sqProcessing || (!sqReady && !SQUARE_MOCK_PAYMENTS)}
          >
            {sqProcessing ? "Processing..." : "Pay with card"}
          </button>
        </div>
      </div>

      <div
        className="jh-card jh-pane"
        style={{
          display:
            applePayAvailable && paymentMethod === "applepay"
              ? "block"
              : "none",
        }}
      >
        <h2 className="jh-pane-title">Apple Pay</h2>
        <div className="jh-note" style={{ marginTop: 6 }}>
          Handmade to order — please allow 2–3 weeks.
        </div>
        {(shipErr || sqErr) && (
          <div
            className="jh-note"
            style={{ color: "#b91c1c", marginBottom: 10 }}
          >
            {shipErr || sqErr}
          </div>
        )}
        <div id="square-apple-pay" />
        <div className="jh-actions" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="jh-btn"
            onClick={payWithSquareApplePay}
            disabled={
              sqProcessing ||
              SQUARE_MOCK_PAYMENTS ||
              !sqApplePayRef.current ||
              !sqReady
            }
          >
            {sqProcessing ? "Processing..." : "Pay with Apple Pay"}
          </button>
        </div>
      </div>

      <div
        className="jh-card jh-pane"
        style={{
          margin: "0 auto",
          display: paymentMethod === "paypal" ? "block" : "none",
        }}
      >
        <h2 className="jh-pane-title">Pay with PayPal</h2>
        <div className="jh-note" style={{ marginTop: 6, marginBottom: 6 }}>
          Handmade to order — please allow 2–3 weeks.
        </div>
        {shipErr && (
          <div
            className="jh-note"
            style={{ color: "#b91c1c", marginBottom: 10 }}
          >
            {shipErr}
          </div>
        )}
        {!API_ROOT && (
          <div className="jh-note" style={{ color: "#b91c1c" }}>
            Set REACT_APP_API_ROOT in your React .env to enable payments.
          </div>
        )}
        <div style={{ width: "100%", maxWidth: 420, margin: "0 auto" }}>
          <PayPalButtons
            style={{ layout: "vertical", shape: "rect" }}
            onClick={async (data, actions) => {
              try {
                const v = validateShipping();
                if (!v.ok) {
                  setShipErr(v.msg);
                  setShipMissing(v.missing || {});
                  try {
                    if (GA4_ID) {
                      trackCheckoutValidationError({
                        measurementId: GA4_ID,
                        missingFields: Object.keys(v.missing || {}),
                        methodAttempted: "paypal",
                        isGuest: !user,
                      });
                    }
                  } catch {}
                  await new Promise((r) => setTimeout(r, 0));
                  return actions.reject();
                }
                return actions.resolve();
              } catch {
                return actions.reject();
              }
            }}
            createOrder={async () => await createOrderServer()}
            onApprove={async (data) => {
              try {
                console.log("[PayPal] onApprove start", {
                  orderID: data?.orderID,
                });
                const result = await captureOrderServer(data.orderID);
                const orderId =
                  result.orderDbId ||
                  `RID-${Date.now().toString(36).toUpperCase()}`;
                console.log("[PayPal] onApprove capture ok", {
                  orderId,
                  orderDbId: result.orderDbId,
                  captureId: result.captureId,
                });
                try {
                  const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
                  if (GA4_ID) {
                    trackPurchase({
                      measurementId: GA4_ID,
                      transactionId: orderId,
                      value: total,
                      tax,
                      shipping: shippingCost,
                      coupon: state.coupon?.code,
                      items: items.map(([k, it]) => ({
                        item_id: String(it.id || it.sku || ""),
                        item_name: String(it.name || ""),
                        price: Number(it.unitPrice) || 0,
                        quantity: Number(it.qty) || 1,
                      })),
                    });
                  }
                } catch {}
                try {
                  sessionStorage.setItem(
                    `order:${orderId}`,
                    JSON.stringify({
                      id: orderId,
                      at: new Date().toISOString(),
                      items: items.map(([k, it]) => it),
                      pricing: {
                        subtotal,
                        discount,
                        tax,
                        shipping: shippingCost,
                        total,
                      },
                      shipping: { ...form },
                      payment: {
                        method: "paypal",
                        payerEmail: result.payerEmail,
                        paypalOrderId: data.orderID,
                        captureId: result.captureId,
                      },
                    }),
                  );
                } catch {}

                console.log("[PayPal] clearing cart", {
                  loggedIn: !!user,
                  userId: user?.id,
                });
                clearCart();

                console.log("[PayPal] redirecting to confirmation", {
                  path: `/order/${orderId}`,
                });
                window.location.assign(`/order/${orderId}`);
              } catch (err) {
                console.error("PayPal error", err);
                try {
                  if (GA4_ID) {
                    trackPaymentError({
                      measurementId: GA4_ID,
                      provider: "paypal",
                      stage: "on_approve",
                      methodAttempted: "paypal",
                      message: String(err?.message || err),
                    });
                  }
                } catch {}
                alert("PayPal error — please try again.");
              }
            }}
            onError={(err) => {
              console.error("PayPal error", err);
              try {
                if (GA4_ID) {
                  trackPaymentError({
                    measurementId: GA4_ID,
                    provider: "paypal",
                    stage: "on_error",
                    methodAttempted: "paypal",
                    message: String(err?.message || err),
                  });
                }
              } catch {}
              alert("PayPal error — please try again.");
            }}
          />
        </div>
      </div>
    </section>
  );
}

export default CheckoutPage;
